import { SaveOutlined } from "@ant-design/icons";
import { App, Button, Modal, Space, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { cleanupTempAttachments } from "../../api/attachmentApi";
import { createDraft, submitDraft, updateDraft, type EntryDraftRecord } from "../../api/draftApi";
import { saveBulk } from "../../api/entryApi";
import { formatInr } from "../utils/currency";
import { BulkEntryGrid, createDailyEntryRow, hasBulkDailyEntryRowData } from "./BulkEntryGrid";
import type { BulkDailyEntryRow } from "./bulkTypes";

export function DailyBulkEntryModal({
  open,
  onClose,
  onSaved,
  draft,
  onDraftSaved
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  draft?: EntryDraftRecord | null;
  onDraftSaved?: () => void;
}) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<BulkDailyEntryRow[]>(() => Array.from({ length: 5 }, createDailyEntryRow));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (draft?.payload?.rows?.length) {
      setRows(draft.payload.rows);
      return;
    }
    setRows(Array.from({ length: 5 }, createDailyEntryRow));
  }, [draft, open]);

  const totals = useMemo(() => {
    const total = rows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
    const cash = rows.filter((x) => x.paymentType === "Cash").reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
    const bank = total - cash;
    return { total, cash, bank };
  }, [rows]);

  const uploadSummary = useMemo(() => {
    const attachments = rows.flatMap((row) => [
      ...(row.billAttachments ?? []),
      ...(row.voucherAttachments ?? [])
    ]);
    return {
      uploading: attachments.filter((item) => item.status === "uploading").length,
      failed: attachments.filter((item) => item.status === "failed").length
    };
  }, [rows]);

  const invalidRowKeys = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => {
      const isActive = !!(row.amount || row.paidOfId || row.entryCategoryId || row.shortRemarkId || row.longRemarks);
      if (!isActive) return;
      const bankInvalid = row.paymentType === "Bank" && (!row.bankAccountId || !row.paymentMode || row.paymentMode === "NA");
      if (!row.entryDate || !row.paidOfId || !row.entryCategoryId || !row.amount || Number(row.amount) <= 0 || bankInvalid) {
        keys.add(row.key);
      }
    });
    return keys;
  }, [rows]);

  const saveDraft = () => {
    const draftRows = rows.map((row) => ({
      ...row,
      billAttachments: (row.billAttachments ?? []).filter((item) => item.status === "completed").map(({ file, previewUrl, ...item }) => item),
      voucherAttachments: (row.voucherAttachments ?? []).filter((item) => item.status === "completed").map(({ file, previewUrl, ...item }) => item)
    }));
    const title = buildDailyDraftTitle(draftRows);
    const saveTask = draft
      ? updateDraft(draft.id, { moduleKey: "DailyEntry", title, payload: { rows: draftRows } })
      : createDraft({ moduleKey: "DailyEntry", title, payload: { rows: draftRows } });

    saveTask
      .then(() => {
        message.success("Draft saved");
        onDraftSaved?.();
      })
      .catch(() => message.error("Unable to save draft"));
  };

  const close = async () => {
    const tempKeys = rows
      .filter((row) => row.billAttachmentIds?.length || row.voucherAttachmentIds?.length)
      .map((row) => row.key);
    if (tempKeys.length) await cleanupTempAttachments(tempKeys);
    onClose();
  };

  const requestClose = () => {
    if (!rows.some(hasBulkDailyEntryRowData)) {
      void close();
      return;
    }

    Modal.confirm({
      title: "Close bulk entry?",
      content: "This bulk entry has unsaved row data. Closing now can discard input that has not been saved as a draft or submitted.",
      okText: "Close Anyway",
      okButtonProps: { danger: true },
      cancelText: "Keep Editing",
      onOk: close
    });
  };

  const validateRows = () => {
    const activeRows = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.amount || row.paidOfId || row.entryCategoryId || row.shortRemarkId || row.longRemarks);
    const errors = activeRows.flatMap(({ row, index }) => {
      const rowNo = index + 1;
      const next: string[] = [];
      if (!row.entryDate) next.push(`Row ${rowNo}: Entry Date is required`);
      if (!row.paidOfId) next.push(`Row ${rowNo}: Paid Of is required`);
      if (!row.entryCategoryId) next.push(`Row ${rowNo}: Entry Category is required`);
      if (!row.amount || Number(row.amount) <= 0) next.push(`Row ${rowNo}: Amount must be greater than zero`);
      if (row.paymentType === "Bank") {
        if (!row.bankAccountId) next.push(`Row ${rowNo}: Bank A/C is required for bank payment`);
        if (!row.paymentMode || row.paymentMode === "NA") next.push(`Row ${rowNo}: Payment Mode is required for bank payment`);
      }
      return next;
    });
    return { activeRows, errors };
  };

  const save = async () => {
    if (uploadSummary.uploading) {
      return message.warning(`Please wait - ${uploadSummary.uploading} file(s) still uploading`);
    }
    if (uploadSummary.failed) {
      return message.warning(`Remove or retry ${uploadSummary.failed} failed upload(s) before saving`);
    }
    const { activeRows, errors } = validateRows();
    if (!activeRows.length) return message.warning("Nothing to save");
    if (errors.length) {
      Modal.warning({
        title: "Fix bulk entry rows",
        width: 620,
        content: (
          <div className="bulk-validation-list">
            {errors.slice(0, 12).map((error) => <div key={error}>{error}</div>)}
            {errors.length > 12 ? <div>And {errors.length - 12} more issue(s).</div> : null}
          </div>
        )
      });
      return;
    }
    const payload = activeRows
      .map(({ row }) => row)
      .map((row) => ({
        paymentEntryDate: row.entryDate,
        paymentPaidDate: row.paidDate || null,
        transactionType: row.transactionType,
        paymentType: row.paymentType,
        paidOfId: row.paidOfId,
        paymentOfId: row.paymentOfId,
        accountLedgerId: row.entryCategoryId,
        shortRemarkId: row.shortRemarkId,
        bankId: row.paymentType === "Bank" ? row.bankId : null,
        bankAccountId: row.paymentType === "Bank" ? row.bankAccountId : null,
        paymentMode: row.paymentType === "Bank" ? row.paymentMode ?? "UPI" : "NA",
        paymentReference: row.paymentType === "Bank" ? row.paymentReference : null,
        amount: row.amount,
        expenseMonthPayment: row.expenseMonthPayment || new Date(row.entryDate).toLocaleString("en-US", { month: "long" }),
        paymentStatus: row.paymentStatus,
        longRemarks: row.longRemarks || row.shortRemarkLabel || "Bulk entry",
        tempKey: row.key
      }));
    setSaving(true);
    try {
      if (draft) await submitDraft(draft.id, payload);
      else await saveBulk("/daily-entries/bulk-upsert", payload);
      message.success("Bulk entries saved");
      onSaved();
      onClose();
    } catch {
      message.error("Unable to save bulk entries");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Daily Entry - Bulk Entry"
      open={open}
      onCancel={requestClose}
      width="100vw"
      className="bulk-entry-modal"
      style={{ top: 0, paddingBottom: 0 }}
      footer={
        <div className="bulk-footer">
          <Space>
            <Button onClick={() => setRows((current) => [...current, createDailyEntryRow()])}>Add Row</Button>
            <Button onClick={saveDraft}>Save Draft</Button>
          </Space>
          <Space>
            <Tag color="red">Total {formatInr(totals.total)}</Tag>
            <Tag color="green">Cash {formatInr(totals.cash)}</Tag>
            <Tag color="blue">Bank {formatInr(totals.bank)}</Tag>
            {uploadSummary.uploading ? <Tag color="processing">Uploading {uploadSummary.uploading}</Tag> : null}
            {uploadSummary.failed ? <Tag color="error">Failed {uploadSummary.failed}</Tag> : null}
            <Button onClick={requestClose}>Close</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!!uploadSummary.uploading || !!uploadSummary.failed} onClick={save}>Save All</Button>
          </Space>
        </div>
      }
    >
      <BulkEntryGrid rows={rows} setRows={setRows} invalidRowKeys={invalidRowKeys} />
    </Modal>
  );
}

function buildDailyDraftTitle(rows: BulkDailyEntryRow[]) {
  const firstActive = rows.find(hasBulkDailyEntryRowData);
  if (!firstActive) return "Daily Entry Draft";
  const date = firstActive.entryDate || "Draft";
  const paidOf = firstActive.paidOfLabel || "Daily Entry";
  return `${date} - ${paidOf}`;
}
